import React, { useEffect, useMemo, useState } from 'react'
import { Button, Chip, TextField } from '@mui/material'
import { toast } from 'react-toastify'
import swal from 'sweetalert'
import { getPermissions } from 'src/utils/Common'
import { permitsList } from 'src/constants/permits'
import {
  getLista, enviarLista, getDestinatarios, crearDestinatario, eliminarDestinatario,
} from 'src/services/preciosService'
import TablaPrecios from './TablaPrecios'

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`

const ListaPreciosTab = () => {
  const [lista, setLista] = useState([])
  const [dests, setDests] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [form, setForm] = useState({ nombre: '', correo: '' })
  const permisos = getPermissions() || {}
  const isAdmin = Boolean(permisos[permitsList.PRECIOS_ADMIN])

  const fetchData = async () => {
    setIsLoading(true)
    const tasks = [getLista()]
    if (isAdmin) tasks.push(getDestinatarios())
    const [l, d] = await Promise.all(tasks)
    if (l && l.success) setLista(l.data)
    if (isAdmin && d && d.success) setDests(d.data)
    setIsLoading(false)
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cambios = lista.filter((r) => String(r.cambio) === '1').length

  const enviar = async () => {
    const ok = await swal({
      title: 'Enviar lista de precios',
      text: `Se enviará el PDF con ${lista.length} precios (${cambios} nuevos) a los socios configurados. ¿Continuar?`,
      buttons: ['Cancelar', 'Enviar'],
    })
    if (!ok) return
    const res = await enviarLista()
    if (res && res.success && res.ok) {
      toast.success(`Lista enviada a ${res.destinatarios} destinatario(s) · ${res.cambios} precios nuevos.`)
      fetchData()
    } else {
      toast.error((res && res.mensaje) || 'No se pudo enviar la lista.')
    }
  }

  const addDest = async () => {
    if (!form.correo) {
      toast.error('Ingresa un correo.')
      return
    }
    const res = await crearDestinatario(form)
    if (res && res.success) {
      toast.success('Destinatario agregado.')
      setForm({ nombre: '', correo: '' })
      fetchData()
    } else {
      toast.error('No se pudo agregar.')
    }
  }

  const delDest = async (id) => {
    const res = await eliminarDestinatario(id)
    if (res && res.success) fetchData()
  }

  const columns = useMemo(
    () => [
      { accessorKey: 'marca', header: 'Marca' },
      { accessorKey: 'modelo', header: 'Modelo' },
      { accessorKey: 'anio_modelo', header: 'Año' },
      { accessorKey: 'pvp_actual', header: 'PVP', Cell: ({ cell }) => fmt(cell.getValue()) },
      { accessorKey: 'bono', header: 'Bono', Cell: ({ cell }) => (String(cell.getValue()) === '0' ? '—' : fmt(cell.getValue())) },
      {
        accessorKey: 'cambio',
        header: 'Estado',
        Cell: ({ cell }) =>
          String(cell.getValue()) === '1' ? <Chip size="small" color="warning" label="Nuevo" variant="outlined" /> : '',
      },
    ],
    [],
  )

  const colsDest = useMemo(
    () => [
      { accessorKey: 'nombre', header: 'Nombre' },
      { accessorKey: 'correo', header: 'Correo' },
      {
        accessorKey: 'accion',
        header: 'Acción',
        Cell: ({ row }) => (
          <Button size="small" color="error" onClick={() => delDest(row.original.id)}>Quitar</Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return (
    <div>
      <h3 style={{ fontSize: 15, marginBottom: 2 }}>Lista de precios para socios</h3>
      <p style={{ fontSize: 12.5, color: '#6b7684', marginTop: 0 }}>
        Envío por lote: se acumulan los cambios y se manda <strong>un solo PDF</strong> con toda la lista vigente
        (precios nuevos resaltados) a los socios. No un correo por cada cambio.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <Chip color={cambios ? 'warning' : 'default'} label={`${cambios} precio(s) sin notificar`} />
        <Button variant="contained" onClick={enviar} disabled={!lista.length}>
          Enviar lista de precios a socios
        </Button>
        <span style={{ fontSize: 11.5, color: '#d9590c' }}>
          En pruebas solo se envía a los correos configurados abajo (no a socios reales).
        </span>
      </div>

      <TablaPrecios columns={columns} data={lista} isLoading={isLoading} pageSize={15} />

      {isAdmin && (
        <div style={{ marginTop: 22 }}>
          <h3 style={{ fontSize: 14 }}>Destinatarios (socios)</h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
            <TextField size="small" label="Nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
            <TextField size="small" label="Correo" style={{ minWidth: 260 }} value={form.correo} onChange={(e) => setForm({ ...form, correo: e.target.value })} />
            <Button variant="outlined" onClick={addDest}>Agregar</Button>
          </div>
          <TablaPrecios columns={colsDest} data={dests} pageSize={10} />
        </div>
      )}
    </div>
  )
}

export default ListaPreciosTab
